import { Router } from '@angular/router';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Events } from '@services/events/events.service';

import {
  SettingsService,
  ResizeHandlerService,
  ObservationService,
  TableService,
  LoadingService,
  CheckResponseService,
  UtilsService
} from '@services';
import { CheckDetailService } from '@services/checkDetail/check-detail.service';
import { ObservationDetailService } from '@services/observationDetail/observation-detail.service';
import { DatePickerConfig, DatePickerObject, ITab, Select2Type } from '@modules/shared/components';
import { Response } from '@modules/management/pages/details/check/models';

import { v4 as uuidv4 } from 'uuid';
import { TranslateService } from '@ngx-translate/core';
import { each, filter, find, get, identity, includes, values, isFunction, last, map, omit } from 'lodash';
import { IObjectStringKeyMap } from '@shared/models';

@Component({
  selector: 'app-check-result-table',
  templateUrl: './check-result-table.page.html',
  styleUrls: ['./check-result-table.page.scss'],
})
export class CheckResultTablePage implements OnInit, OnDestroy {
  public checkName: string;
  public tabs: ITab[] = [
    {value: 'SHARED.Completed', key: 'complete', postfix: '(0)'},
    {value: 'SHARED.Comments_Small', key: 'logged', postfix: '(0)'},
    {value: 'SHARED.Corrective_Actions', key: 'corrective', postfix: '(0)'},
    {value: 'DASHPAGES.Unable_To_Perform', key: 'skipped', postfix: '(0)'},
    {value: 'SHARED.Missed', key: 'missed', postfix: '(0)'}
  ];

  public tableIds = {
    complete: `complete_${uuidv4()}`,
    logged: `logged_${uuidv4()}`,
    corrective: `corrective_${uuidv4()}`,
    skipped: `skipped_${uuidv4()}`,
    missed: `missed_${uuidv4()}`
  };

  public backNavigation: boolean;
  public currentTab: any = this.tabs[0];
  public targetConfig: any = {
    data: [],
    value: null,
    options: {}
  };
  public datePickerConfig: DatePickerConfig = {
    data: [
      {id: 'day', text: this.translate.instant('SHARED.Today')},
      {id: 'yesterday', text: this.translate.instant('SHARED.Yesterday')},
      {id: 'week', text: this.translate.instant('SHARED.This_Week')},
      {id: 'month', text: this.translate.instant('SHARED.This_Month')},
      {id: 'quarter', text: this.translate.instant('SHARED.This_Quarter')},
      {id: '30days', text: this.translate.instant('SHARED.30_days')},
      {id: '60days', text: this.translate.instant('SHARED.60_days')},
      {id: '90days', text: this.translate.instant('SHARED.90_days')},
      {id: 'custom', text: this.translate.instant('SHARED.Custom')}
    ]
  };
  private currentFilter: IObjectStringKeyMap<any> = { endTime: null, startTime: null };
  private currentTeam: any;
  private checkID: number;
  private targetSignature: string;
  private flagIssues: any;
  private tableOffset = 0;
  private isLoading: boolean;
  private responses: Response[];
  private routeSub: Subscription;

  constructor(
    private utils: UtilsService,
    private translate: TranslateService,
    private activatedRoute: ActivatedRoute,
    private settingsService: SettingsService,
    private resizeHandler: ResizeHandlerService,
    private checkDetailService: CheckDetailService,
    private router: Router,
    private observationService: ObservationService,
    private observationDetailService: ObservationDetailService,
    private loadingService: LoadingService,
    private tableService: TableService,
    private checkResponseService: CheckResponseService,
    private events: Events,
    private location: Location
  ) {}

  ngOnInit() {
    this.initDatePicker();
    this.backNavigation = this.activatedRoute.snapshot.paramMap.get('backNavigation') === 'true';

    // CVX-6242: do not rely on ionViewWillEnter/ionViewDidEnter to drive the load.
    // Reached from the asset detail Check History row, this page's Ionic lifecycle
    // hooks are never dispatched. That was established by instrumenting them: neither
    // ionViewWillEnter nor ionViewDidEnter fires, checkObservationListener is never
    // called, getCheckResponseByCheckIDStatus is called zero times, and the host
    // element keeps `ion-page-invisible` — so the page rendered every tab as (0) with
    // no request and no error.
    //
    // WHY the hooks are not dispatched on that path is NOT established. Do not state
    // a mechanism here unless you have verified it; an earlier revision of this
    // comment asserted a nested ion-router-outlet, and there is no such outlet in
    // this app (only app.component.html and layout.component.html declare one).
    //
    // The route is the one signal present on every entry path, so key off it rather
    // than depending on the hooks at all.
    this.routeSub = this.activatedRoute.paramMap.subscribe(() => {
      this.enterPage();
      this.buildTargetDropDown();
      this.checkObservationListener();
    });
  }

  ngOnDestroy() {
    this.routeSub?.unsubscribe();
    this.releasePageResources();
  }

  ionViewWillLeave() {
    this.releasePageResources();
  }

  // Safe to run twice — both paths above fire on a normal navigation. Events.unsubscribe
  // splices a single occurrence and no-ops when absent; removeResizeHandler no-ops for a
  // name that is not registered. This balances because enterPage() holds exactly one
  // ccs:checkResponseUpdate registration regardless of how often it runs; if that ever
  // stops being true, the extra listener survives on an Ionic-cached page (QA round 2).
  private releasePageResources() {
    this.events.unsubscribe('ccs:checkResponseUpdate', this.checkObservationListener);
    // initDataTable registers one resize handler per tab key, so release those.
    // 'conditionTable' was removed here previously but is never registered by this
    // page — it belongs to ConditionTablePage / CaTablePage.
    each(this.tabs, (tab) => this.resizeHandler.removeResizeHandler(tab.key));
  }

  ionViewWillEnter() {
    // A second, deliberately redundant trigger for entries where Ionic does fire its
    // hooks. Whether a cached instance re-entered by back-navigation ALSO re-emits
    // paramMap is NOT established: QA round 2 read @ionic/angular's router-outlet
    // (activateWith -> updateActivatedRouteProxy -> currentActivatedRoute$.next, with the
    // component-injected route being a proxy whose paramMap re-switchMaps on that
    // emission) and concluded it probably does, but two reviewers disagreed and nothing
    // has measured it. Do not restate either answer here as fact.
    //
    // This call does not depend on the answer. enterPage() is idempotent — it assigns
    // fields and holds exactly one ccs:checkResponseUpdate registration — so running it
    // once or twice per entry is equally correct.
    this.enterPage();
  }

  private enterPage() {
    this.currentFilter = this.checkDetailService.statusPageTimeRange.currentFilter;
    this.checkID = +this.activatedRoute.snapshot.paramMap.get('id');
    this.targetSignature = this.activatedRoute.snapshot.paramMap.get('target');

    // CVX-6285: read the shared team scope on every entry. Guarding this with
    // `if (!this.currentTeam)` meant a cached instance kept the team from a
    // previous navigation even after the new caller cleared it.
    //
    // CVX-6367: resolve it rather than reading the raw value. On a cold entry --
    // browser refresh or deep link -- the singleton was never populated, so the
    // raw read is `undefined` and the visibility filter treats that as "do not
    // scope by team", rendering responses from teams the viewer is not in.
    //
    // Resolved per table: the asset drill-down's "no team scope" applies only to
    // the table it opened, so a null it left in the singleton cannot unscope a
    // different table reached later. That is why the route params are read first.
    this.currentTeam = this.checkDetailService.resolveResultTableTeamScope(this.checkID, this.targetSignature);
    if (!this.datePickerConfig.value) {
      if (this.checkDetailService.statusPageTimeRange.id) {
        this.datePickerConfig.value = this.checkDetailService.statusPageTimeRange.id;
      } else {
        this.applyTimeFilterPeriodByType({ type: 'day' as Select2Type, range: null });
        this.datePickerConfig.value = this.checkDetailService.statusPageTimeRange.id;
      }
    }
    const activeTab = this.activatedRoute.snapshot.queryParamMap.get('activeTab');
    if (activeTab) {
      this.currentTab = filter(this.tabs, {key: activeTab})[0];
    }
    const checkType = this.settingsService.getItem('checkType', this.checkID);
    if (checkType) {
      this.checkName = checkType.messageTitle;
    } else {
      this.checkName = 'Site-Wide';
    }

    // CVX-6242 / QA round 2: enterPage() is reachable from BOTH the ngOnInit paramMap
    // subscription and ionViewWillEnter, so subscribing unconditionally registered two
    // handlers. Teardown removes one per call — Events.unsubscribe splices a single
    // occurrence — and on a FORWARD navigation Ionic caches this page: ionViewWillLeave
    // fires but ngOnDestroy does not. One live listener therefore survived on the hidden
    // page, and ccs:checkResponseUpdate is published app-wide on every cached response
    // refresh, so it raised a LoadingController overlay and a redundant paginated fetch
    // over whatever page the user had actually moved to.
    //
    // Drop any existing registration first so this holds exactly one however many times
    // it runs. Note this is NOT what the sibling check-type-table.page.ts does: that page
    // relies on a balanced hook pair (subscribe in ionViewWillEnter, unsubscribe in
    // ionViewWillLeave, one each per transition) and would double-register if its enter
    // hook ever ran twice. That option is unavailable here — the hooks are precisely what
    // does not fire on the asset-detail entry path — so drop-before-add is what makes the
    // count independent of which triggers happen to run.
    this.events.unsubscribe('ccs:checkResponseUpdate', this.checkObservationListener);
    this.events.subscribe('ccs:checkResponseUpdate', this.checkObservationListener);
  }

  ionViewDidEnter() {
    this.buildTargetDropDown();
    this.checkObservationListener();
  }

  public onPeriodChange(dateConfig: DatePickerObject): void {
    if (dateConfig.type) {
      this.applyTimeFilterPeriodByType(dateConfig);
      this.checkObservationListener();
    }
  }

  public onTabChange(val) {
    setTimeout(() => {
      this.initDataTable(val.key);
    }, 100);
  }

  public back() {
    this.datePickerConfig.value = null; // clear out drop down
    if (this.backNavigation) {
      this.location.back()
    } else {
      this.router.navigate([`pages/dashboard/check-type-table/${this.checkID}`]);
    }
  }

  public onTargetChange(event) {
    this.targetSignature = event.value;
    this.checkObservationListener();
  }

  private checkObservationListener = async () => {
    if (!this.isLoading) {
      this.isLoading = true;

      try {
        await this.loadingService.enable();
        this.responses = values(await this.checkResponseService.getCheckResponseByCheckIDStatus(this.checkID, this.currentFilter, this.currentTeam, this.targetSignature)) || [];
        await this.getLoggedFlaggedIssues()
          .then(async () => {
            await this.countOfEachTable();
            this.loadingService.disable();
            this.initDataTable(this.currentTab.key);
          })
          .catch(() => this.loadingService.disable());
      } finally {
        // CVX-6242: the first two awaits sit outside the .catch chain above, so a
        // rejection from either (e.g. an IndexedDB/storage failure inside the response
        // fetch) used to leave isLoading true for the life of the component, silently
        // turning every later timespan and target change into a no-op.
        // disable() checks getTop() first, so calling it again here is a no-op when
        // the loader was already dismissed on the success path.
        this.loadingService.disable();
        this.isLoading = false;
      }
    }
  };

  private async getLoggedFlaggedIssues() {
    const tableData = await this.checkDetailService.prepareIssueCaTable('logged', this.responses);
    const flaggedIssueIds: number[] = map(tableData, 'flaggedIssue');

    return flaggedIssueIds.length ? this.checkResponseService.getFlagIssue(flaggedIssueIds).then((flagIssues) => {
      this.flagIssues = flagIssues;
    }) : Promise.resolve();
  }

  private applyTimeFilterPeriodByType(dateConfig: DatePickerObject): void {
    this.checkDetailService.statusPageTimeRange.id = dateConfig.type;
    const times = dateConfig?.range || this.utils.timespan(dateConfig.type);
    const dateType = dateConfig.type as string;
    if (dateType !== 'yesterday') {
      this.currentFilter.startTime = times.startTime;
      this.currentFilter.endTime = null;
    } else if (dateType === 'yesterday') {
      this.currentFilter.startTime = times.startTime;
      this.currentFilter.endTime = times.endTime;
    } else {
      this.currentFilter = omit(this.currentFilter, ['startTime', 'endTime']);
    }
  }

  private async countOfEachTable() {
    each(['logged', 'corrective'], async data => {
      const tab: any = find(this.tabs, {key: data});
      const items = await this.checkDetailService.prepareIssueCaTable(data, this.responses);
      tab.postfix = '(' + items.length + ')';
    });
    each(['missed', 'skipped', 'complete'], async data => {
      const tab: any = find(this.tabs, {key: data});
      const item = await this.checkDetailService.prepareUnableMissed(data, this.currentFilter, this.checkID, this.currentTeam, this.targetSignature, this.responses);
      tab.postfix = '(' + item.length + ')';
    });
  }

  private async initDataTable(id) {
    const idReference: string = '#' + this.tableIds[id];
    let tableData: any = [];
    // get data by table type
    if (includes(['logged', 'corrective'], id)) {
      // get data for logged and corrective action
      tableData = await this.checkDetailService.prepareIssueCaTable(id, this.responses);

      if (id === 'logged') {
        const flaggedIssueIds: number[] = map(tableData, 'flaggedIssue');

        if (flaggedIssueIds.length) {
          each(tableData, (tableDataItem) => {
            const flaggedIssue = this.flagIssues[tableDataItem.flaggedIssue];

            if (flaggedIssue) {
              tableDataItem.notes = map(this.checkDetailService.getNotes(flaggedIssue), (note) => {
                // `displayText` is the translated text; `data` is the original.
                // The column renders `displayText` and falls back to `value`.
                note.value = note.data;
                return note;
              });
            }
            // NO else-branch here. `tableDataItem` is the CACHED answer object
            // (prepareIssueCaTable pushes respObj.answers[key] by reference), so
            // assigning resolved notes onto it overwrote the compliance original
            // in the shared cache — issue-detail then read the translation as if
            // it were the original and lost the Show original control entirely.
            // notesColumn resolves for display instead, without mutating.
          });
        }
      }
    }

    if (includes(['skipped', 'missed', 'complete'], id)) {
      // get data for logged and corrective action
      tableData = await this.checkDetailService.prepareUnableMissed(id, this.currentFilter, this.checkID, this.currentTeam, this.targetSignature, this.responses);
    }

    const preparedData = map(tableData, (item) => [
      ...map(this.checkDetailService.tableMap[id], (tableMapItem) => {
        if (isFunction(tableMapItem.data)) {
          return tableMapItem.data(item);
        }
        return get(item, tableMapItem.data);
      }),
      item
    ]);

    const columns = map(this.checkDetailService.tableMap[id], (column) => identity({
      title: column.title,
      render: column.render
    }));

    if ($(`${idReference} thead`).length) {
      const table: any = $(idReference).DataTable();
      table.clear().rows.add(preparedData).draw(false).columns.adjust();

      setTimeout(() => {
        table.columns.adjust().draw(true);
      });
      return;
    }

    const pagePadding = 100;
    const tableOffset: number = $(idReference).offset().top;
    if (tableOffset) {
      this.tableOffset = tableOffset;
    }
    const scrollY: number = $(window).height() - this.tableOffset - pagePadding;

    const table: any = $(idReference).DataTable(<any>{
      data: preparedData,
      stateSave: true,
      destroy: true,  // kills table and draws new when new data set comes in
      paging: true,  // imp for virtual scroll
      searching: false,  // top search bar for DT
      info: false,
      deferRender: true, // part of the plugin for virtual scroll, paging= true
      scroller: { // part of virtual scroll
        rowHeight: 65,
        displayBuffer: 18,
        loadingIndicator: true,
        boundaryScale: 0.15
      },
      scrollY: `${scrollY}px`,
      dom: 'Blfrtip', // initailized button elements
      buttons: {
        buttons: [
          $.extend(true, {}, {   // settings on how exported XLS should look like
            exportOptions: {
              columns: ':visible',
              orthogonal: 'export', // check on column builder to see how export type is defined
              stripNewlines: 'false',
            }
          }, {
            extend: 'excel',
            text: this.translate.instant('SHARED.Save_to_Excel'),
            className: 'button-styled'
          }),
          // {
          //   extend: 'colvis',  // settings for select/hide columns
          //   columns: ':not(.no-vis)',
          //   text: this.translate.instant('MGMT_LIST.Select_Columns'),
          //   className: 'button-styled',
          //   collectionLayout: 'three-column',
          // }
        ]
      },
      columnDefs: [{
        visible: false,
        targets: [columns.length]
      }],
      language: {
        emptyTable: this.translate.instant('SHARED.No_Activity'),
        sZeroRecords: this.translate.instant('SHARED.sZeroRecords')
      },
      scrollX: true,
      order: [[0, 'desc']],
      columns
    });

    $(`${idReference} tbody`).off('click').on('click', 'tr', (event: Event) => {
      const data = last(table.row(event.currentTarget).data());
      this.checkDetailService.currentTableData = map(table.rows().data(), (rowItems) => last(rowItems));
      this.navByTableType(data);
    });

    $(idReference).width('100%');
    $(table.table().header()).addClass('table-header sorting');

    setTimeout(() => {
      table.columns.adjust().draw(true);
      this.tableService.handleResize('.dataTables_scrollBody:visible:last', 50, null);
      this.resizeHandler.addResizeHandler(id, () => this.tableService.handleResize('.dataTables_scrollBody:visible:last', 50, null));
    });
  }

  private async navByTableType(data) {
    // let's save the filter
    this.checkDetailService.statusPageTimeRange.currentFilter = this.currentFilter;
    switch (this.currentTab.key) {
      case 'logged': {
        // this would need detailed logged issue page
        this.router.navigate([`pages/dashboard/issue-detail/${data.responseID}/${data.flaggedIssue}`], {queryParams: { backNavigation: this.backNavigation }});
        break;
      }
      case 'corrective': {
        const observation = await this.observationService.getObservation(data.observationID);
        const queryParam = {
          superTaskBoard: !this.backNavigation,
          headerIsHidden: true,
          checkType: data.checkType,
          targetSignature: data.targetSignature
        };
        // let's clean the oids for CA detail back and forth
        const dataSet = map(this.checkDetailService.currentTableData, 'observationID');
        // CVX-5916: getObservation resolves null when the observation was archived/removed.
        if (observation) {
          this.observationDetailService.navigateToDetail(observation, dataSet, !this.backNavigation, true, queryParam);
        }
        //  this.router.navigate([`pages/dashboard/ca-detail/${observation.uuid}`])
        break;
      }
      case 'unable':
      case 'skipped':
      case 'complete': {
        this.router.navigate([`pages/dashboard/check-detail/${data.responseID}`], {queryParams: {skipFooter: this.backNavigation}});
        break;
      }
    }
  }

  private buildTargetDropDown() {
    const selectMenuObj: any = {
      data: this.checkDetailService.buildTargetMenu(),
      value: this.targetSignature,
      options: {
        minimumResultsForSearch: Infinity,
        sorter: (data) => data
      }
    };
    this.targetConfig = selectMenuObj;
  }

  private initDatePicker() {
    if (this.checkDetailService.statusPageTimeRange.id) {
      this.datePickerConfig.value = this.checkDetailService.statusPageTimeRange.id;
    }

    if (this.checkDetailService.statusPageTimeRange.currentFilter) {
      this.datePickerConfig.range = this.checkDetailService.statusPageTimeRange.currentFilter;
    }
  }
}
